import {Button, Grid} from "@mui/material";
import "dayjs/locale/it";
import {RefObject, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";
import {useCommercialCategoryRepository} from "../../../../api/repositories/CommercialCategoryRepository";
import {useUpsellingRateRepository} from "../../../../api/repositories/UpsellingRateRepository";
import {CommercialCategorySelectFE} from "../../../../api/types/commercialCategory";
import {SelectElementModelBE, SelectElementModelFE} from "../../../../api/types/generic";
import {
    FormUpsellingRateModel,
    UpsellingRateEntityRequest,
    UpsellingRateEntityResponse,
} from "../../../../api/types/upsellingRate";
import CustomBanner from "../../../../common/components/CustomBanner";
import {FormAutocomplete, FormDateTimePicker, FormTextField} from "../../../../common/form-fields";
import {commercialSegmentListAtom} from "../../../../state/commercialSegmentsAtom";
import {upsellingRateCreationAtom} from "../../../../state/upsellingRatesRecoil";
import {
    fromGenericBeToGenericFe,
    fromSingleGenericBeToSingleGenericFe,
    fromSingleGenericFeToSingleGenericBe,
} from "../../../../utils";
import {BASE_DEFINITION_FORM_STEPS, SET_SNACKBAR} from "../../../../constants";
import {useFormContext} from "react-hook-form";
import DealerAndTenant from "../../../../common/components/DealerAndTenant";

type UpsellingRateBaseDefinitionProps = { submitRef: RefObject<HTMLButtonElement>, refresh: Function };

const UpsellingRateBaseDefinition = ({submitRef, refresh}: UpsellingRateBaseDefinitionProps) => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Use Form Context
    const {register, setValue, handleSubmit, watch, getValues} = useFormContext<FormUpsellingRateModel>();

    // Get the currently selected legal entity
    const selectedLegalEntity = watch("legalEntity");
    const activeTenantId = selectedLegalEntity?.code ? String(selectedLegalEntity.code) : undefined; // Ensure activeTenantId is string or undefined

    // Use Repository Hooks
    const {putUpsellingRateList, postUpsellingRateList} = useUpsellingRateRepository();

    // Use Repository Hooks
    const {getCommercialCategoriesByCommercialSegmentId} = useCommercialCategoryRepository();

    // Use Recoil
    const commercialSegmentList = useRecoilValue(commercialSegmentListAtom);
    const [upsellingRateCreation, setUpsellingRateCreation] = useRecoilState(upsellingRateCreationAtom);
    const {readOnly, definitionData, inUse} = upsellingRateCreation;

    const getCategoryList = async (value: SelectElementModelFE, currentTenantId?: string) => {
        const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((value as SelectElementModelFE)?.code as string);
        const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
        setCommercialCategoryListOriginal(commercialCategoryList);
    };

    useEffect(() => {
        setValue("id", definitionData?.id as number)
        setValue("name", definitionData?.name as string)
        setValue("description", definitionData?.description as string)
        setValue("commercialSegmentId", definitionData?.commercialSegmentId as SelectElementModelFE)
        setValue("commercialCategory", definitionData?.commercialCategory as CommercialCategorySelectFE)
        setValue("from", definitionData?.from as string)
        setValue("to", definitionData?.to as string)
        setValue("used", definitionData?.used as boolean)
        if (definitionData?.legalEntity) {
            setValue("legalEntity", {
                code: definitionData?.legalEntity?.code as string,
                label: t('translation:legalEntities.'.concat(definitionData?.legalEntity?.code as string))
            })
        }
        if (definitionData?.dealer) {
            setValue("dealer", {
                code: definitionData?.dealer?.code as string,
                label: t('translation:dealers.'.concat(definitionData?.dealer?.code as string))
            })
        }
        getCategoryList(definitionData?.commercialSegmentId as SelectElementModelFE, activeTenantId)
    }, [activeTenantId, definitionData])

    // Use State
    const [commercialCategoryList, setCommercialCategoryList] = useState<SelectElementModelFE[]>([]);
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);

    // Effect per filtrare le categorie quando cambia la legal entity
    useEffect(() => {
        const selectedLegalEntityCode = activeTenantId 
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : undefined);
        
        if (!selectedLegalEntityCode || commercialCategoryListOriginal.length === 0) {
            setCommercialCategoryList([]);
            return;
        }

        // Espandi le categorie in base alla legal entity selezionata
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        commercialCategoryListOriginal.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === selectedLegalEntityCode) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [activeTenantId, commercialCategoryListOriginal, user.legalEntities]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        setValue("commercialSegmentId", value);
        setValue("commercialCategory", null);
        
        if (value) {
            const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((value as SelectElementModelFE).code as string);
            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryListOriginal(commercialCategoryList);
        } else {
            setCommercialCategoryListOriginal([]);
        }
    };

    const getDefinitionData = (data: UpsellingRateEntityResponse) => ({
        ...data,
        commercialCategory: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory
        ) as CommercialCategorySelectFE,
        commercialSegmentId: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ) as SelectElementModelFE,
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const confirmDefinition = async (values: FormUpsellingRateModel) => {
        const newUpsellingRateList: UpsellingRateEntityRequest = {
            commercialCategory: fromSingleGenericFeToSingleGenericBe(values.commercialCategory as SelectElementModelFE),
            name: values.name,
            from: values.from,
            to: values.to,
            description: values.description,
            used: inUse,
            dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : values.dealer?.code as string,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string,
        };

        try {
            const creationResponse = definitionData?.id
                ? await putUpsellingRateList({
                    id: definitionData?.id,
                    ...JSON.parse(JSON.stringify(newUpsellingRateList)),
                })
                : await postUpsellingRateList(newUpsellingRateList);

            setUpsellingRateCreation({
                ...upsellingRateCreation,
                upsellingRateId: creationResponse.id,
                step: BASE_DEFINITION_FORM_STEPS.ASSOCIATION,
                completed: [true, ...upsellingRateCreation.completed.slice(1)],
                definitionData: getDefinitionData(creationResponse)
            });
            refresh();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {inUse && <CustomBanner message={t("pricing:upsellingRate.banner.definition")} severity={"warning"}/>}
            <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                {/* NAME UPSELLINGRATE LIST */}
                <Grid item xs={12} md={3}>
                    <FormTextField
                        {...register("name")}
                        label={`${t("pricing:upsellingRate.form.labels.name")}*`}
                        readOnly={readOnly || inUse}
                        autoFocus
                    />
                </Grid>
                {/* DESCRIPTION EXCESS_GROUP LIST */}
                <Grid item xs={12} md={3}>
                    <FormTextField
                        {...register("description")}
                        label={`${t("pricing:upsellingRate.form.labels.description")}*`}
                        readOnly={readOnly || inUse}
                    />
                </Grid>
                {/* COMMERCIAL SEGMENTS */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("commercialSegmentId")}
                        label={`${t("pricing:upsellingRate.form.labels.commercialSegment")}*`}
                        options={commercialSegmentList}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                        readOnly={readOnly || inUse}
                        handleChange={handleChangeCommercialSegment}
                    />
                </Grid>
                {/* COMMERCIAL CATEGORY */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("commercialCategory")}
                        label={`${t("pricing:upsellingRate.form.labels.commercialCategory")}*`}
                        options={commercialCategoryList}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                        readOnly={readOnly || inUse || Boolean(!watch("commercialSegmentId"))}
                    />
                </Grid>
                {/* RANGE EXCESS_GROUP LIST */}
                {/* FROM */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("from")}
                        label={`${t("pricing:upsellingRate.form.labels.startDateValidity")}*`}
                        readOnly={readOnly || inUse}
                    />
                </Grid>
                {/* TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("to")}
                        label={`${t("pricing:upsellingRate.form.labels.endDateValidity")}*`}
                        minDate={watch("from")}
                        readOnly={readOnly}
                    />
                </Grid>
                <DealerAndTenant
                    readOnly={readOnly}
                    md={3}
                    legalEntityName={"legalEntity"}
                    dealerName={"dealer"}
                    dealerIsVisible={true}
                    idForReadOnly={getValues("id")}
                />
            </Grid>
            <Button ref={submitRef} sx={{display: "none"}} onClick={handleSubmit(confirmDefinition)}/>
        </Grid>
    );
};

export default UpsellingRateBaseDefinition;
